import { type Component, For, createResource, Show, createMemo } from "solid-js"
import { useParams } from "@solidjs/router"
import type { WorkplaceId, MonthString, WorkType } from "@eckersdorf/api"
import { getWorkplaces, getWorkers, getSchedules } from "../lib/api"
import { useAuth } from "../lib/auth"

// Polish day names (short)
const DAY_NAMES: Record<number, string> = {
  0: "Nd",
  1: "Pn",
  2: "Wt",
  3: "Śr",
  4: "Cz",
  5: "Pt",
  6: "So",
}

export const PrintPage: Component = () => {
  const params = useParams()
  const { user } = useAuth()

  const workplaceId = createMemo(() => params["workplaceId"] as WorkplaceId)
  const month = createMemo(() => params["month"] as MonthString)

  // Fetch data
  const [workplaces] = createResource(user, () => getWorkplaces())
  const [workers] = createResource(
    () => ({ user: user(), workplaceId: workplaceId() }),
    async ({ workplaceId }) => getWorkers(workplaceId)
  )
  const [schedules] = createResource(
    () => ({ user: user(), workplaceId: workplaceId(), month: month() }),
    async ({ workplaceId, month }) => getSchedules(workplaceId, month)
  )

  const workplaceName = createMemo(() => 
    workplaces()?.find(w => w.id === workplaceId())?.name ?? "Harmonogram"
  )

  const scheduleData = createMemo(() => {
    const s = schedules()
    if (!s) return {}
    const data: Record<string, Record<string, Record<string, WorkType | null>>> = {}
    for (const schedule of s.schedules) {
      if (data[schedule.workerId] === undefined) data[schedule.workerId] = {}
      const workerData = data[schedule.workerId]
      if (workerData !== undefined) {
        workerData[schedule.date] = schedule.scheduleJson
      }
    }
    return data
  })

  // Generate all days of the month
  const getDaysInMonth = createMemo(() => {
    const m = month()
    if (!m) return []
    const parts = m.split("-")
    const year = parseInt(parts[0] || "2024", 10)
    const monthNum = parseInt(parts[1] || "1", 10)
    const daysInMonth = new Date(year, monthNum, 0).getDate()

    const days: Array<{
      dateStr: string
      dayOfMonth: number
      dayOfWeek: number
      isWeekend: boolean
    }> = []

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, monthNum - 1, d)
      const dayOfWeek = date.getDay()
      const dateStr = `${year}-${String(monthNum).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      
      days.push({
        dateStr,
        dayOfMonth: d,
        dayOfWeek,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6
      })
    }
    return days
  })

  const getCellContent = (workerId: string, dateStr: string) => {
    const workerSchedule = scheduleData()[workerId]
    if (!workerSchedule) return null
    const daySchedule = workerSchedule[dateStr]
    if (!daySchedule) return null

    const types = new Set<WorkType>()
    let minHour = 24
    let maxHour = -1
    let workType: "seller" | "butcher" | null = null

    const hours = Object.keys(daySchedule).sort()
    for (const hStr of hours) {
      const type = daySchedule[hStr]
      if (type) {
        types.add(type)
        const h = parseInt(hStr, 10)
        if (h < minHour) minHour = h
        if (h > maxHour) maxHour = h
        if (type === "seller" || type === "butcher") {
          workType = type
        }
      }
    }

    if (types.size === 0) return null
    if (types.has("holiday")) return <span class="font-bold">Św</span>
    if (types.has("vacation")) return <span class="font-bold">U</span>
    if (types.has("sickLeave")) return <span class="font-bold">Ch</span>
    if (types.has("wolne")) return <span class="font-bold">W</span>

    if (minHour <= maxHour && workType) {
      const label = workType === "seller" ? "S" : "L"
      return (
        <div class="flex flex-col items-center leading-tight">
          <span class="text-[10px] font-medium">{minHour}-{maxHour + 1}</span>
          <span class="text-[8px] uppercase">{label}</span>
        </div>
      )
    }
    return null
  }

  // Get short name for worker (initial + last name)
  const getShortName = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}.${lastName}`
  }

  return (
    <div class="print-root bg-white min-h-screen text-black font-sans">
      <style>
        {`
          @media print {
            @page {
              size: portrait;
              margin: 6mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            .print-page {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
          
          .vertical-text {
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            white-space: nowrap;
          }
          
          table { 
            border-collapse: collapse; 
            width: 100%; 
          }
          th, td { 
            border: 1px solid black; 
            padding: 2px 3px;
            text-align: center;
            vertical-align: middle;
          }
          th { 
            background-color: #f3f4f6; 
            font-size: 9px; 
          }
          td { 
            font-size: 10px;
            height: 20px;
          }
          
          .cell-weekend { background-color: #f5f5f5; }
          .cell-sunday { background-color: #fee2e2; }
          .header-cell { 
            min-width: 28px;
            padding: 4px 2px;
          }
          .date-cell {
            width: 50px;
            min-width: 50px;
            text-align: left;
            padding-left: 4px;
            font-weight: bold;
            font-size: 10px;
          }
        `}
      </style>

      {/* Control bar (hidden in print) */}
      <div class="no-print p-4 bg-gray-100 border-b flex justify-between items-center sticky top-0">
        <div>
          <h1 class="text-lg font-bold">{workplaceName()} - {month()} (Podgląd wydruku)</h1>
          <p class="text-sm text-gray-500">Użyj opcji przeglądarki (Ctrl+P), aby wydrukować. Format: Portrait A4.</p>
        </div>
        <button 
          onClick={() => window.print()}
          class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-bold"
        >
          Drukuj teraz
        </button>
      </div>

      <div class="p-2">
        {/* Document Header */}
        <div class="mb-3 border-b-2 border-black pb-2">
          <h1 class="text-xl font-bold uppercase tracking-wide">{workplaceName()}</h1>
          <h2 class="text-base mt-1 text-gray-700">Harmonogram pracy: {month()}</h2>
        </div>

        <Show when={workers() && schedules()}>
          <div class="print-page">
            <table>
              <thead>
                <tr>
                  {/* Date column header */}
                  <th class="date-cell">Data</th>
                  {/* Worker columns - vertical names */}
                  <For each={workers()}>
                    {(worker) => (
                      <th class="header-cell">
                        <div class="vertical-text mx-auto" style={{ height: "80px" }}>
                          {getShortName(worker.firstName, worker.lastName)}
                        </div>
                      </th>
                    )}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={getDaysInMonth()}>
                  {(day) => (
                    <tr>
                      {/* Date cell */}
                      <td class={`date-cell ${day.dayOfWeek === 0 ? 'cell-sunday' : day.isWeekend ? 'cell-weekend' : ''}`}>
                        {String(day.dayOfMonth).padStart(2, "0")} {DAY_NAMES[day.dayOfWeek]}
                      </td>
                      {/* Worker cells */}
                      <For each={workers()}>
                        {(worker) => (
                          <td class={day.dayOfWeek === 0 ? 'cell-sunday' : day.isWeekend ? 'cell-weekend' : ''}>
                            {getCellContent(worker.id, day.dateStr)}
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          
          {/* Legend */}
          <div class="mt-4 border-t border-black pt-2 text-xs">
            <h3 class="font-bold uppercase mb-1">Legenda</h3>
            <div class="flex flex-wrap gap-4">
              <div><b>6-14 S</b> - Sklep</div>
              <div><b>6-14 L</b> - Lada</div>
              <div><b>U</b> - Urlop</div>
              <div><b>Ch</b> - Chorobowe</div>
              <div><b>Św</b> - Święto</div>
              <div><b>W</b> - Wolne</div>
            </div>
          </div>
        </Show>
      </div>
    </div>
  )
}
